import { useCallback } from 'react'
import { blobToBase64 } from '@/services/audioUtils'
import { logger } from '@/lib/logging'
import type { SessionRefs } from './types'

export function useVideoStreaming(refs: SessionRefs) {
  return useCallback(() => {
    if (refs.frameIntervalRef.current) clearInterval(refs.frameIntervalRef.current)

    const interval = refs.isScreenSharingRef.current ? 100 : 500
    logger.info(`Starting video streaming with interval: ${interval}ms`)

    refs.frameIntervalRef.current = window.setInterval(() => {
      const video = refs.videoRef.current
      const canvas = refs.canvasRef.current
      const service = refs.serviceRef.current

      if (!video || !canvas || !service || video.readyState < 2) return

      try {
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const maxWidth = 1920
        let width = video.videoWidth
        let height = video.videoHeight

        if (width > maxWidth) {
          const scale = maxWidth / width
          width = maxWidth
          height *= scale
        }

        canvas.width = width
        canvas.height = height
        ctx.drawImage(video, 0, 0, width, height)

        canvas.toBlob(
          async (blob) => {
            if (!blob || !refs.serviceRef.current) return

            try {
              refs.serviceRef.current.sendVideoFrame(await blobToBase64(blob))
            } catch (err) {
              logger.warn('Failed to send video frame, stopping stream', { error: String(err) })
              if (refs.frameIntervalRef.current) clearInterval(refs.frameIntervalRef.current)
              refs.frameIntervalRef.current = null
            }
          },
          'image/jpeg',
          0.7
        )
      } catch {
        if (refs.frameIntervalRef.current) clearInterval(refs.frameIntervalRef.current)
        refs.frameIntervalRef.current = null
      }
    }, interval)
  }, [refs])
}
